import { useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { collaboratorAllocationsQueryKeys } from './useCollaboratorAllocationsQuery';
import type { CollaboratorAllocationFormValues } from '@/features/Collaborators/Allocations/components/CollaboratorAllocationForm/CollaboratorAllocationForm';
import { AllocationDurationTypes, AllocationTypes } from '@/features/Collaborators/Models';
import { useAuth } from '@/contexts/AuthContext';

export interface UpdateCollaboratorAllocationVariables {
  collaboratorId: string;
  allocationId: string;
  values: CollaboratorAllocationFormValues;
}

function sanitizeBucket(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number(value ?? 0);

  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }

  return numeric;
}

export function useUpdateCollaboratorAllocationMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      collaboratorId,
      allocationId,
      values,
    }: UpdateCollaboratorAllocationVariables) => {
      if (!collaboratorId) {
        throw new Error('Colaborador não informado.');
      }

      if (!allocationId) {
        throw new Error('Alocação não informada.');
      }

      if (!values.startDate) {
        throw new Error('Data de início é obrigatória.');
      }

      if (!user) {
        throw new Error('Usuário não autenticado.');
      }

      const startDate = Timestamp.fromDate(values.startDate);
      const endDate = values.endDate ? Timestamp.fromDate(values.endDate) : null;

      if (values.durationType === AllocationDurationTypes.Defined && !endDate) {
        throw new Error('Data de fim é obrigatória quando a duração é definida.');
      }

      const payload = {
        allocationType: values.allocationType,
        durationType: values.durationType,
        bucketType: values.bucketType,
        bucket: sanitizeBucket(values.bucket),
        startDate,
        endDate,
        clientId: values.clientId ?? null,
        clientName: values.clientName ?? null,
        clientLogo: values.clientLogo ?? null,
        clientResponsible: values.clientResponsible ?? null,
        departmentId: values.departmentId ?? null,
        departmentName: values.departmentName ?? null,
        projectId: values.projectId ?? null,
        projectName: values.projectName ?? null,
        capacityId: values.capacityId ?? null,
        capacityLabel: values.capacityLabel ?? null,
        capacityTypeId: values.capacityTypeId ?? null,
        capacityTypeName: values.capacityTypeName ?? null,
        capacityTechnologyId: values.capacityTechnologyId ?? null,
        capacityTechnologyName: values.capacityTechnologyName ?? null,
        capacitySeniorityId: values.capacitySeniorityId ?? null,
        capacitySeniorityName: values.capacitySeniorityName ?? null,
        rate: values.rate ?? null,
        noticeReason: values.allocationType === AllocationTypes.Product ? values.noticeReason?.trim() || null : null,
        responsibleId: values.responsibleId ?? null,
        responsibleName: values.responsibleName ?? null,
        responsibleAvatar: values.responsibleAvatar ?? null,
        updatedById: user.uid,
        updatedByName: user.displayName ?? user.email ?? 'Usuário Raro',
        updatedByAvatar: user.photoURL ?? null,
        updatedAt: Timestamp.now(),
      };

      const allocationRef = doc(
        db,
        'collaborators_private',
        collaboratorId,
        'allocations',
        allocationId
      );

      await updateDoc(allocationRef, payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: collaboratorAllocationsQueryKeys.list(variables.collaboratorId),
      });
      queryClient.invalidateQueries({
        queryKey: collaboratorAllocationsQueryKeys.detail(
          variables.collaboratorId,
          variables.allocationId
        ),
      });
    },
  });
}

export type UseUpdateCollaboratorAllocationMutationResult = ReturnType<
  typeof useUpdateCollaboratorAllocationMutation
>;
